// =============================================================================
// io/sync/multiSourceRead.ts — the trades the two-logger detector measures.
// -----------------------------------------------------------------------------
// ── ⚠ ITS OWN READ, AND THE ALTERNATIVE WAS WORSE ──
//
// The detector needs one thing no existing read carries: WHICH LOGGER pushed the
// trade, which lives at `extras.ingest.source`. Adding it to the shared table
// read would have meant a fifth entry in `EXTRAS_ANNEX` — and D128 records what
// that costs: the annex list and `tableRowFromDb` are two places that must agree
// and neither knows about the other, so `extensions` was added, typed, selected,
// returned by PostgREST, and copied by NOTHING. A card read "$0" against 316 rows
// that had one, and `tsc` was clean throughout.
//
// ▶ A purpose-built read has none of that surface. Six small fields, and every
// consumer of the table row is untouched.
//
// ── 🚨 IT IS BOOK-WIDE, AND THAT IS THE POINT ──
//
// Every other read on this tab rides the global date filter. This one must not.
// The harm it names took SIX WEEKS to notice (D148) — a warning scoped to "this
// week" would go quiet the moment the trader looks at anything else, which is
// exactly when they would conclude it was fixed.
//
// ⚠ THE COST, STATED RATHER THAN DISCOVERED: six columns, and the rows are
// filtered SERVER-SIDE to those that arrived by realtime push
// (`extras->ingest` not null). On the beta user's book that was 592 of ~3,300
// trades when D148 measured it. A file import or a hand-entered trade is
// excluded by the same filter, which is also what the detector wants — see
// `SourcedTrade.ingestSource`.
//
// ⚠ NOT MEASURED AGAINST THE LIVE DATABASE. D148's 592/18/0 figures came from a
// query run against the real book; this SELECT has not been. The shape is the
// same and the filter is narrower, but that is reasoning, not a measurement.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { PAGE_ROWS } from './load';
import { reportFailure, reportFailureAs } from '../diagnostics/errorLog';

/**
 * ⚠ `extras->ingest->>source` — the `->>` is load-bearing. `->` would return
 * the JSON scalar `"sierra"` WITH its quotes; `->>` returns the text. The
 * endpoint writes `extras.ingest = { source, externalId }`
 * (`trade-ingest/index.ts`), and only `source` is read here.
 */
export const MULTI_SOURCE_COLUMNS: readonly string[] = [
  'account_id', 'source_account', 'date', 'qty', 'net_pnl',
  'ingestSource:extras->ingest->>source',
];

export const MULTI_SOURCE_SELECT: string = MULTI_SOURCE_COLUMNS.join(',');

/** Exactly the shape `domain/multiSource` reads, and nothing more. */
export interface MultiSourceTrade {
  accountId: string | null;
  sourceAccount: string | null;
  date: string | null;
  qty: number | null;
  netPnl: number | null;
  ingestSource: string | null;
}

export interface MultiSourceRead {
  trades: MultiSourceTrade[];
  /** Null on success. ⚠ A failure is NOT an empty book — see below. */
  error: string | null;
}

/**
 * Every realtime-pushed trade in the book, oldest first.
 *
 * ⚠ RETURNS ITS ERROR RATHER THAN THROWING, and reports it on the way past. The
 * caller is a warning card: it must be able to say "couldn't check" instead of
 * rendering the all-clear it would otherwise show for an empty list. A silent
 * empty here reads as "no conflict", which is the one wrong answer.
 *
 * ── 🚨 THE `try` IS NOT DECORATION, AND ITS ABSENCE WAS CAUGHT BY THE SUITE ──
 *
 * The first version handled only the Supabase `error` FIELD, so it honoured that
 * promise for a rejected query and broke it for everything else — a client that
 * throws, a network stack that throws, a stub whose builder is missing a method.
 * The Accounts suite went **75 passing with 73 unhandled rejections**: the tests
 * still passed, because an unhandled promise rejection is not a test failure.
 *
 * ▶ A function whose whole contract is "I will not throw" has to mean it for the
 * throw it did not think of.
 */
export async function loadMultiSourceTrades(
  sb: SupabaseClient,
): Promise<MultiSourceRead> {
  const trades: MultiSourceTrade[] = [];

  try {
    for (let from = 0; ; from += PAGE_ROWS) {
      const { data, error } = await sb
        .from('trades')
        .select(MULTI_SOURCE_SELECT)
        // ⚠ SERVER-SIDE. Filtering in the client would pull the whole book to
        // discard most of it, and this read is deliberately book-wide.
        .not('extras->ingest', 'is', null)
        .order('date', { ascending: true })
        .range(from, from + PAGE_ROWS - 1);

      if (error) {
        return {
          // ⚠ Whatever arrived so far is DISCARDED. A partial answer here is
          // worse than none: the detector reports "no conflict" for any
          // account-day the missing page held, and a warning that under-reports
          // is one nobody can trust the silence of.
          trades: [],
          error: reportFailureAs(
            'two-logger check', error,
            `Couldn’t check for two loggers on one account: ${error.message}`,
          ),
        };
      }
      if (!data || !data.length) break;

      for (const r of data as unknown as Record<string, unknown>[]) {
        trades.push({
          accountId: (r['account_id'] as string | null) ?? null,
          sourceAccount: (r['source_account'] as string | null) ?? null,
          date: (r['date'] as string | null) ?? null,
          qty: (r['qty'] as number | null) ?? null,
          netPnl: (r['net_pnl'] as number | null) ?? null,
          // ⚠ Arrives under its ALIAS, not a column name — the D128 trap, which
          // is why this mapper is hand-written rather than a loop over a column
          // map, which cannot see aliases.
          ingestSource: (r['ingestSource'] as string | null) ?? null,
        });
      }

      if (data.length < PAGE_ROWS) break;
    }
  } catch (e) {
    return {
      trades: [],
      error: reportFailure('two-logger check', e),
    };
  }

  return { trades, error: null };
}
